import React, { useState } from 'react';
import { Chip, Colors, Text } from 'library';
import { Stack } from 'library/ui';
import { styled, IconButton } from '@mui/material';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { ArrowForward } from '@mui/icons-material';
import { formatMonthDayYear } from 'shared/Dates';
import { BasicCard } from './BasicCard';
import { WorkerTab } from './WorkerTab';
import type { ActionNeededWorker } from 'parent-portal/home/new/types';
import ComplianceActionInfoModal from 'parent-portal/jobs/upcoming/BusinessCalendar/ComplianceActions/ComplianceActionInfoModal';

export function ActionNeededWorkers({
    loading,
    small,
    workers,
    refresh,
}: {
    loading: boolean;
    small: boolean;
    workers?: ActionNeededWorker[];
    refresh: () => void;
}) {
    const [selectedWorker, setSelectedWorker] = useState<ActionNeededWorker>();

    function onClick(value: ActionNeededWorker) {
        setSelectedWorker(value);
    }

    if (!workers || workers.length === 0) return null;

    return (
        <BasicCard loading={loading} small={true}>
            <Row>
                <Column width="90%">
                    <Text variant="h1">Action Needed</Text>
                    <Text variant="body2">
                        Please complete the below actions to ensure your program remains in compliance.
                    </Text>
                </Column>
                <Column width="10%">
                    <a
                        href="https://chatwoot.join-tandem.com/hc/child-care-center-help-center/articles/1742826037-what-if-my-booked-worker-has-action-required-next-to-their-name-learn-more-about-initiating-resubmissions-in-clearinghouse"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Icon>
                            <Question />
                        </Icon>
                    </a>
                </Column>
            </Row>
            <Stack gap="sm" align="start">
                {workers.map((worker) => (
                    <WorkerTab
                        key={worker.user.id}
                        worker={{
                            user: worker.user,
                            profile_image: worker.user.profile_picture,
                            middle: (
                                <>
                                    Booked at {worker.business_location.name} starting{' '}
                                    <b>{formatMonthDayYear(worker.shift_date)}</b>
                                </>
                            ),
                            bottom: (
                                <Chip
                                    label={worker.action.replaceAll('_', ' ').startCase()}
                                    backgroundColor={Colors.mediumRed}
                                    textColor={Colors.white}
                                    style={{ padding: 0, minHeight: 25, maxHeight: 25, fontSize: 10, marginTop: 5 }}
                                />
                            ),
                        }}
                        small={small}
                        onClick={() => onClick(worker)}
                        icon={ArrowForward}
                    />
                ))}
            </Stack>
            {selectedWorker ? (
                <ComplianceActionInfoModal
                    provider_user={selectedWorker?.user}
                    business_location_id={selectedWorker?.business_location.id}
                    actionText={selectedWorker?.action}
                    modalOpen={Boolean(selectedWorker)}
                    setModalOpen={() => setSelectedWorker(undefined)}
                    refresh={refresh}
                />
            ) : null}
        </BasicCard>
    );
}

const Row = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 20,
    justifyContent: 'space-between',
    width: '100%',
});

const Question = styled(QuestionMark)({
    padding: 5,
});

const Icon = styled(IconButton)({
    color: Colors.white,
    backgroundColor: Colors.mediumGrey,
    height: 20,
    width: 20,
    marginRight: 10,
    marginBottom: 2,
    '&:hover': {
        backgroundColor: Colors.mediumGrey,
    },
});

const Column = styled('div')(({ width }: { width: string }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: width,
    justifyContent: 'center',
}));
